import { useCallback, useState } from "react"
import { Box, Button, IconButton, Tooltip, Typography } from "@mui/material"
import { Building2, Edit, MapPin, Plus, Trash2 } from "lucide-react"

import { ConfirmDialog } from "@/components/ui/confirm-dialog"
import { useToast } from "@/components/ui/use-toast"
import { DealerFormDialog } from "@crm-src/components/reseller-dealer-form-dialog"
import { ResellerNetworkColumn } from "@crm-src/components/reseller-network-column"
import usePermissions from "@crm-src/hooks/usePermissions"
import { useResellerPagedList } from "@crm-src/hooks/useResellerPagedList"
import dealersApiService from "@crm-src/lib/dealers-api"
import type { CreateDealerData, Dealer } from "@crm-src/types/dealer"

const EMPTY_FORM: CreateDealerData = {
  name: "",
  address: "",
  city: "",
  state: "",
  postal_code: "",
  country: "",
  website: "",
}

interface ResellerDealersListProps {
  resellerId: string
}

/**
 * The Dealers column of a reseller's Network tab.
 *
 * Was a card that fetched EVERY dealer and rendered them into a 300px scroll
 * box with no search. It now shares `<ResellerNetworkColumn>` with the
 * Customers and Prospects columns beside it, so all three search and page the
 * same way and none of them can disturb another's query state.
 */
export function ResellerDealersList({ resellerId }: ResellerDealersListProps) {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  const [editingDealer, setEditingDealer] = useState<Dealer | null>(null)
  const [deletingDealer, setDeletingDealer] = useState<Dealer | null>(null)
  const [formData, setFormData] = useState<CreateDealerData>(EMPTY_FORM)
  const [submitting, setSubmitting] = useState(false)
  const { toast } = useToast()
  const { isAdmin } = usePermissions()

  const list = useResellerPagedList<Dealer>(
    useCallback(
      async ({ page, perPage, search }) => {
        const result = await dealersApiService.getPageByReseller(resellerId, {
          page,
          per_page: perPage,
          ...(search ? { search } : {}),
        })
        return {
          rows: result.dealers,
          totalCount: result.pagination.total_count,
          totalPages: result.pagination.total_pages,
        }
      },
      [resellerId],
    ),
  )

  const failed = (error: unknown, fallback: string): void => {
    const message =
      (error as { response?: { data?: { error?: string } } })?.response?.data?.error || fallback
    toast({ variant: "destructive", title: "Error", description: message })
  }

  const handleCreate = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    if (!formData.name) return

    setSubmitting(true)
    try {
      await dealersApiService.create(resellerId, formData)
      setFormData(EMPTY_FORM)
      setIsAddDialogOpen(false)
      // Re-fetch rather than splicing the row in: the new dealer may not belong
      // on the page currently shown (the list is name-ordered and paged), and a
      // row appended to page 3 of 4 is a row in the wrong place.
      list.refresh()
      toast({ title: "Success", description: "Dealer created successfully" })
    } catch (error) {
      failed(error, "Failed to create dealer")
    } finally {
      setSubmitting(false)
    }
  }

  const handleUpdate = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    if (!editingDealer || !formData.name) return

    setSubmitting(true)
    try {
      await dealersApiService.update(editingDealer.id, formData)
      setEditingDealer(null)
      setFormData(EMPTY_FORM)
      list.refresh()
      toast({ title: "Success", description: "Dealer updated successfully" })
    } catch (error) {
      failed(error, "Failed to update dealer")
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async (id: string): Promise<void> => {
    try {
      await dealersApiService.delete(id)
      list.refresh()
      toast({ title: "Success", description: "Dealer deleted successfully" })
    } catch (error) {
      failed(error, "Failed to delete dealer")
    }
  }

  const openEditDialog = (dealer: Dealer): void => {
    setEditingDealer(dealer)
    setFormData({
      name: dealer.name,
      address: dealer.address || "",
      city: dealer.city || "",
      state: dealer.state || "",
      postal_code: dealer.postal_code || "",
      country: dealer.country || "",
      website: dealer.website || "",
    })
  }

  return (
    <>
      <ResellerNetworkColumn<Dealer>
        data-cy="reseller-dealers-column"
        title="Dealers"
        icon={<Building2 size={18} />}
        entityNoun="dealers"
        emptyMessage="No dealers added yet"
        list={list}
        {...(isAdmin()
          ? {
              action: (
                <Tooltip describeChild title="Add a new dealer for this reseller">
                  <Button
                    variant="outlined"
                    color="inherit"
                    size="small"
                    startIcon={<Plus size={12} />}
                    onClick={() => {
                      setFormData(EMPTY_FORM)
                      setIsAddDialogOpen(true)
                    }}
                  >
                    Add Dealer
                  </Button>
                </Tooltip>
              ),
            }
          : {})}
        renderRow={(dealer) => {
          const fullAddress = [dealer.address, dealer.city, dealer.state, dealer.postal_code, dealer.country]
            .filter(Boolean)
            .join(", ")

          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                p: 1.5,
                bgcolor: "surfaceSoft",
                borderRadius: 1,
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 500, fontSize: 14 }}>{dealer.name}</Typography>
                {fullAddress && (
                  <Box sx={{ display: "flex", alignItems: "center", fontSize: 12, color: "text.secondary" }}>
                    <Box sx={{ mr: 0.5, flexShrink: 0, display: "flex" }}>
                      <MapPin size={12} />
                    </Box>
                    <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {fullAddress}
                    </Box>
                  </Box>
                )}
              </Box>

              {isAdmin() && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                  <Tooltip title="Edit dealer information">
                    <IconButton size="small" onClick={() => openEditDialog(dealer)}>
                      <Edit size={12} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete this dealer">
                    <IconButton
                      size="small"
                      sx={{ color: "error.main", "&:hover": { color: "error.dark" } }}
                      onClick={() => setDeletingDealer(dealer)}
                    >
                      <Trash2 size={12} />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>
          )
        }}
      />

      <ConfirmDialog
        isOpen={!!deletingDealer}
        onClose={() => setDeletingDealer(null)}
        title="Delete Dealer"
        description={
          deletingDealer
            ? `Are you sure you want to delete ${deletingDealer.name}? This action cannot be undone.`
            : ""
        }
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={async () => {
          if (deletingDealer) await handleDelete(deletingDealer.id)
        }}
      />

      <DealerFormDialog
        open={isAddDialogOpen}
        mode="create"
        value={formData}
        submitting={submitting}
        onChange={setFormData}
        onClose={() => setIsAddDialogOpen(false)}
        onSubmit={handleCreate}
      />

      <DealerFormDialog
        open={!!editingDealer}
        mode="edit"
        value={formData}
        submitting={submitting}
        onChange={setFormData}
        onClose={() => setEditingDealer(null)}
        onSubmit={handleUpdate}
      />
    </>
  )
}
